<template>
  <main class="integration-list">
    <modal-dialog
      content-class="integration-modal-dialog"
      v-bind:title="integrationTitle"
      v-model="isShownModal"
      with-cross
    >
      <div class="integration-modal-dialog__content">
        <template v-if="selectedIntegrationId === $options.integrationServices.fdoc.id">
          <p>
            Для безбумажной подписи документов необходимо
            <a
              v-bind:href="$options.integrationServices.fdoc.linkToService"
              target="_blank"
              rel="noopener noreferrer">подключиться к сервису F.doc</a>
          </p>
          <p>
            Подробности можно узнать у вашего менеджера:
            <a v-bind:href="$options.supportPhoneHref">
              {{ $options.supportPhoneTitle }}
            </a>
          </p>
        </template>
        <template v-else>
          <p>{{ integrationText }}</p>
          <p>
            Татьяна
            <a v-bind:href="$options.supportPhoneHref">
              {{ $options.supportPhoneTitle }}
            </a>
          </p>
        </template>
      </div>
    </modal-dialog>
    <div class="integration-list__container">
      <h1 class="integration-list__title">Интеграции</h1>
      <div class="integration-list__items">
        <integration-item
          class="integration-list__item"
          v-for="{ integration, routeName } in integrations"
          v-bind:key="integration.id"
          v-bind:route-name="routeName"
          v-bind:tags="integration.tags"
          v-on:click="onIntegrationClick(integration)"
        >
          <span class="integration-list__item__caption">{{integration.title}}</span>
          <span
            v-if="integration.description"
            class="integration-list__item__description"
          >
            {{ integration.description }}
          </span>
        </integration-item>
      </div>
    </div>
  </main>
</template>

<script>
import ModalDialog from '@/components/ModalDialog.vue';
import aboutMixin from '@/mixins/aboutMixin';

/**
 * @description Элемент списка.
 * @type {object}
 */
const IntegrationItem = {
  props: {
    routeName: { default: () => '', type: String },
    tags: { required: true, type: Array },
  },
  name: 'IntegrationItem',
  render(createElement) {
    if (this.tags.includes('settings') && !this.tags.includes('disabled')) {
      return createElement(
        'router-link',
        { props: { to: { name: this.routeName } } },
        this.$slots.default,
      );
    }
    return createElement(
      'button',
      { attrs: { type: 'button' }, on: { click: () => this.$emit('click') } },
      this.$slots.default,
    );
  },
};

/**
 * @description Название маршрута по секции.
 * @type {object}
 */
const ROUTE_NAME_BY_SECTION = {
  'aggregator-integrations': 'ClinicPanelIntegration32top',
  'appointment-booking-widget': 'ClinicPanelAppointmentBookingWidget',
  'uis-integration-settings': 'ClinicPanelIntegrationUis',
  'mango-office-integration-settings': 'ClinicPanelIntegrationMangoOffice',
  'prodoctorov-integrations': 'ClinicPanelIntegrationProdoctorov',
  'fdoc-integrations': 'ClinicPanelIntegrationFDoc',
  messengers: 'ClinicPanelIntegrationChatPush',
};

const integrationServices = {
  fdoc: {
    id: 'fdoc-integrations',
    linkToService: 'https://fdoc.ru/referral/?utm_source=partner&utm_medium=referral&utm_campaign=1232&utm_content=32top',
  },
};

/**
 * @description Страница списка интеграций.
 * @type {object}
 */
export default {
  components: {
    IntegrationItem,
    ModalDialog,
  },
  computed: {
    integrations() {
      return this.integrationList.map((integration) => ({
        integration,
        routeName: ROUTE_NAME_BY_SECTION[integration.id] || '',
      }));
    },
    integrationTitle() {
      return this.selectedIntegrationDisabled
        ? `Модуль ${this.selectedIntegrationTitle} не подключен`
        : `Модуль ${this.selectedIntegrationTitle} подключен`;
    },
    integrationText() {
      return this.selectedIntegrationDisabled
        ? 'Для подключения модуля свяжитесь с вашим менеджером:'
        : 'Возникли вопросы по использованию сервиса? Обратитесь по номеру:';
    },
  },
  created() {
    this.fetchIntegrations();
  },
  data() {
    return {
      integrationList: [],
      isShownModal: false,
      isShownModalHelp: false,
      selectedIntegrationDisabled: false,
      selectedIntegrationId: '',
      selectedIntegrationTitle: '',
    };
  },
  methods: {
    /**
     * @description Получить интеграции.
     * @returns {void}
     */
    fetchIntegrations() {
      const onResponse = (integrations) => {
        this.integrationList = integrations;
      };
      this
        .$suggestionGateway
        .viewAllIntegrations()
        .then((result) => result.forEach(onResponse));
    },
    /**
     * @description Обработать клик по интеграции.
     * @param {{title:string}} integration
     * @returns {void}
     */
    onIntegrationClick({ id, title, tags }) {
      this.selectedIntegrationDisabled = !!tags.includes('disabled');
      this.isShownModal = true;
      this.selectedIntegrationId = id;
      this.selectedIntegrationTitle = title;
    },
  },
  mixins: [aboutMixin],
  integrationServices,
  name: 'ClinicPanelIntegrationList',
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

::v-global(.integration-modal-dialog .v-card__title) {
  margin: 0;
  color: var(--reading-color);
}
.integration-modal-dialog {
  &__content {
    font-size: 1rem;
    color: var(--reading-color);
  }
  p {
    display: block;
  }
  a:hover {
    text-decoration: none;
  }
}

.integration-list {
  width: 100%;
  height: 100vh;
  overflow-y: scroll;
  &__container {
    padding: 38px 20px 48px 23px;
    width: 100%;
    max-width: 1187px;
  }
  &__title {
    color: #000000;
    margin: 0;
  }
  &__items {
    margin-top: 20px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 12px;
  }
  &__item {
    text-align: center;
    padding: 0 5px;
    @include buttons.btn-transparent(100%, 100px, 18px);
    text-decoration: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
    &__caption {
      font-weight: 700;
    }
    &__description {
      font-size: 1rem;
      color: #707683;
    }
  }
}
@media screen and (max-width: 480px) {
  .integration-list {
    &__items {
      grid-template-columns: 1fr;
    }
  }
}
</style>
